import React from 'react';
import { formatNumberFR, formatPriceFR } from '../../utils/formatFR';
import Bloc from './Bloc';
import { COULEUR_MARCHE, COULEUR_PORTEFEUILLE } from './couleurs';

/** « moins de 10 » en petite étiquette sur une ligne (lot DI2 § 3). */
const MoinsDeDix = () => (
  <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 whitespace-nowrap">
    moins de 10
  </span>
);

const Prix = ({ ligne }) => {
  if (!ligne.mediane) return <MoinsDeDix />;
  return <span className="num whitespace-nowrap">{formatPriceFR(ligne.mediane)}</span>;
};

const Ecart = ({ valeur }) => {
  if (valeur === null || valeur === undefined) return <span className="text-slate-400">—</span>;
  const alerte = valeur >= 15;
  return (
    <span className={`num font-semibold whitespace-nowrap ${alerte ? 'text-amber-600' : 'text-slate-700'}`}>
      {valeur > 0 ? '+' : ''}{valeur} %
    </span>
  );
};

/** En-tête d'une colonne du portefeuille : carré orange + texte normal. */
const EnTetePortefeuille = ({ libelle, classes = '' }) => (
  <th className={`text-right font-semibold py-2 align-bottom text-slate-500 ${classes}`}>
    <span className="inline-flex items-center gap-1 justify-end leading-tight">
      <span className="inline-block w-2 h-2 rounded-sm shrink-0"
            style={{ backgroundColor: COULEUR_PORTEFEUILLE }}></span>
      {libelle}
    </span>
  </th>
);

const Stock = ({ valeur }) => {
  if (valeur === null || valeur === undefined || valeur === 0) {
    return <span className="text-slate-400">—</span>;
  }
  return (
    <span className="num whitespace-nowrap">
      {valeur < 1 ? '< 1 mois' : `${formatNumberFR(valeur)} mois`}
    </span>
  );
};

/** Tableau « Par activité » ou « Par zone » — un clic applique le filtre (lot DI § 5). */
export const TableauObservatoire = ({
  titre, legende, colonne, lignes = [], portefeuille, activiteChoisie, avecStock = false,
  onClic, testid,
}) => {
  const maximum = Math.max(1, ...lignes.map((l) => l.nombre || 0));
  const colonnesPortefeuille = Boolean(portefeuille);

  return (
    <Bloc titre={titre} legende={legende} pleineLargeur testid={testid}>
      {lignes.length === 0 ? (
        <p className="text-sm text-slate-400 py-6 text-center">Aucune ligne à afficher.</p>
      ) : (
        <div className="min-w-0">
          <table className="w-full table-fixed text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className={`text-left font-semibold py-2 align-bottom ${colonnesPortefeuille ? 'w-[26%]' : 'w-[34%]'}`}>
                  {colonne}
                </th>
                <th className="text-right font-semibold py-2 align-bottom w-[14%]">Ventes</th>
                {/* mini-barre et fourchette : seulement interrupteur éteint (lot DI2 § 3) */}
                {!colonnesPortefeuille && (
                  <>
                    <th className="text-left font-semibold py-2 hidden md:table-cell w-[14%]"></th>
                    <th className="text-right font-semibold py-2 align-bottom w-[22%]">Prix médian</th>
                    <th className="text-right font-semibold py-2 align-bottom hidden md:table-cell">
                      La moitié entre
                    </th>
                  </>
                )}
                {colonnesPortefeuille && (
                  <>
                    <th className="text-right font-semibold py-2 align-bottom hidden md:table-cell w-[18%]">
                      Prix médian
                    </th>
                    <EnTetePortefeuille libelle="Vos fonds" classes="w-[12%]" />
                    <EnTetePortefeuille libelle="Prix demandé" classes="hidden md:table-cell w-[16%]" />
                    <EnTetePortefeuille libelle="Écart" classes="w-[10%]" />
                    {avecStock && (
                      <EnTetePortefeuille libelle="Stock (mois)" classes="hidden md:table-cell w-[12%]" />
                    )}
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {lignes.map((ligne) => {
                const mien = (portefeuille || {})[String(ligne.cle)] || {};
                return (
                  <tr
                    key={ligne.cle}
                    onClick={() => onClic && onClic(ligne)}
                    data-testid={`${testid}-ligne-${ligne.cle}`}
                    className="border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50"
                  >
                    <td className="py-3 pr-2 min-h-[44px]">
                      <span className="text-slate-800 font-medium break-words">{ligne.libelle}</span>
                      {ligne.peu_de_ventes && (
                        <span className="block text-[10px] text-slate-400">peu de ventes</span>
                      )}
                    </td>
                    <td className="py-3 text-right num text-slate-700 whitespace-nowrap">
                      {formatNumberFR(ligne.nombre)}
                    </td>
                    {!colonnesPortefeuille && (
                      <>
                        <td className="py-3 hidden md:table-cell">
                          <span className="block h-2 rounded-full" style={{
                            width: `${Math.max(4, Math.round((100 * ligne.nombre) / maximum))}%`,
                            backgroundColor: COULEUR_MARCHE, opacity: 0.8,
                          }}></span>
                        </td>
                        <td className="py-3 text-right text-slate-800"><Prix ligne={ligne} /></td>
                        <td className="py-3 text-right text-slate-500 hidden md:table-cell num whitespace-nowrap">
                          {ligne.p25 && ligne.p75
                            ? `${formatPriceFR(ligne.p25)} – ${formatPriceFR(ligne.p75)}`
                            : '—'}
                        </td>
                      </>
                    )}
                    {colonnesPortefeuille && (
                      <td className="py-3 text-right text-slate-800 hidden md:table-cell">
                        <Prix ligne={ligne} />
                      </td>
                    )}
                    {colonnesPortefeuille && (
                      <>
                        <td className="py-3 text-right num font-semibold text-slate-800 whitespace-nowrap">
                          {formatNumberFR(mien.nombre || 0)}
                        </td>
                        <td className="py-3 text-right hidden md:table-cell num text-slate-700 whitespace-nowrap">
                          {/* lot DJ B2 — 0 bien → « — » ; « moins de 10 » = 1 à 9 biens */}
                          {mien.mediane_demandee
                            ? formatPriceFR(mien.mediane_demandee)
                            : (mien.nombre ? <MoinsDeDix /> : <span className="text-slate-400">—</span>)}
                        </td>
                        <td className="py-3 text-right"><Ecart valeur={mien.ecart_pct} /></td>
                        {avecStock && (
                          <td className="py-3 text-right hidden md:table-cell">
                            <Stock valeur={mien.stock_mois} />
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {colonnesPortefeuille && !activiteChoisie && (
            <p className="text-xs text-slate-500 mt-3">
              Les prix demandés par ligne restent affichés ; choisissez une activité pour comparer
              l'ensemble de votre portefeuille.
            </p>
          )}
        </div>
      )}
    </Bloc>
  );
};

export default TableauObservatoire;
